import { createFileRoute, Link } from "@tanstack/react-router"; // FORCE_RELOAD_DASHBOARD_V4_FINAL
import { SEO } from "@/components/SEO";
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { cn } from "@/lib/utils";
import { trackWhatsAppClick } from "@/lib/gtm";

export const Route = createFileRoute("/produtos")({
  component: ProductsPage,
});

function ProductsPage() {
  const products = [
    {
      title: 'Janela Sobreposta',
      description: 'Solução acústica instalada sobre a janela existente, ideal para reduzir ruídos externos sem precisar remover a esquadria atual.',
      benefits: [
        'Instalação sem quebra-quebra',
        'Redução de ruídos externos',
        'Mantém a janela existente',
        'Ideal para apartamentos, quartos, salas e escritórios',
        'Acabamento discreto e sob medida'
      ],
      image: '/janela-sobreposta.webp'
    },
    {
      title: 'Janela de correr acústica',
      description: 'Opção prática e elegante para quem busca conforto acústico, boa vedação e aproveitamento de espaço.',
      benefits: ['Praticidade e isolamento eficiente.'],
      image: '/janela-correr-v5.webp'
    },
    {
      title: 'Janela com persiana integrada',
      description: 'Solução que combina controle de luminosidade, privacidade e isolamento acústico em um único sistema para ambientes residenciais e comerciais.',
      benefits: ['Controle de luz e conforto acústico.'],
      image: '/produto02.webp'
    },
    {
      title: 'Janela Maxim-Ar',
      description: 'Modelo funcional e sofisticado, muito utilizado em corredores, áreas sociais e ambientes que precisam de ventilação com boa vedação.',
      benefits: ['Vedação reforçada e estética preservada.'],
      image: '/janela-maxim-ar-acustica.webp'
    },
    {
      title: 'Janela de Giro',
      description: 'Ideal para quem busca alto desempenho acústico e abertura ampla, com vedação por compressão e instalação rápida.',
      benefits: ['Máxima performance acústica.'],
      image: '/produto04.webp'
    },
  ];

  return (
    <>
      <SEO 
        title="Janelas e esquadrias acústicas no Rio de Janeiro | STOP SOM"
        description="Conheça janelas acústicas, esquadrias acústicas e soluções antirruído sob medida para residências, apartamentos e empresas no Rio de Janeiro."
      />
      <div className="bg-white">
      {/* Hero Interno */}
      <section className="pt-40 pb-20 bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20" />
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl text-center lg:text-left mx-auto lg:mx-0"
          >
            <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold mb-6">Janelas e esquadrias acústicas sob medida RJ</h1>
            <p className="text-2xl opacity-90 leading-relaxed">
              Soluções antirruído para casas, apartamentos, escritórios, consultórios e ambientes comerciais.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-8 md:py-16 px-6 bg-gray-50 text-center">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl md:text-3xl lg:text-5xl font-bold text-primary mb-6">Janelas e esquadrias acústicas sob medida no Rio de Janeiro</h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            A STOP SOM Janelas Acústicas oferece janelas e esquadrias acústicas sob medida para reduzir ruídos externos e melhorar o conforto de diferentes ambientes no Rio de Janeiro. Nossos projetos combinam vedação eficiente, materiais adequados e acabamento profissional.
          </p>
        </div>
      </section>

      {/* Products List */}
      <section className="py-12 md:py-24 px-6">
        <div className="container mx-auto">
          <div className="space-y-32">
            {products.map((product, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={cn(
                  "flex flex-col gap-12 items-center",
                  i % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                )}
              >
                <div className="lg:w-1/2">
                  <div className="relative">
                    <div className="absolute inset-0 bg-accent/10 rounded-[2.5rem] rotate-3 transform" />
                    <img 
                      src={product.image} 
                      alt={product.title} 
                      width="800"
                      height="600"
                      loading="lazy"
                      className="relative rounded-[2.5rem] shadow-2xl w-full h-auto object-contain"
                    />
                  </div>
                </div>
                <div className="lg:w-1/2 text-center lg:text-left">
                  <h3 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">{product.title}</h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-6">{product.description}</p>
                  <div className="space-y-3 mb-10">
                    {product.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-start justify-center lg:justify-start gap-3">
                        <div className="bg-secondary/10 p-1.5 rounded-full text-secondary shrink-0">
                          <CheckCircle size={18} />
                        </div>
                        <p className="font-semibold text-primary text-sm md:text-base">{benefit}</p>
                      </div>
                    ))}
                  </div>
                  <Link 
                    to="/orcamento" 
                    onClick={() => trackWhatsAppClick("Solicitar orçamento", "/orcamento", "produtos")}
                    data-gtm-event="whatsapp_click"
                    data-conversion="whatsapp"
                    data-button-location="produtos"
                    className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-xl font-bold hover:bg-secondary transition-all"
                  >
                    Solicitar orçamento <ArrowRight size={20} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bloco Escolha Ideal */}
      <section className="py-12 md:py-24 bg-[#1c6874] text-white px-6">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="text-center lg:text-left">
              <h2 className="text-2xl md:text-3xl lg:text-5xl font-bold mb-8">Como escolher o modelo ideal?</h2>
              <p className="text-lg opacity-80 mb-10 leading-relaxed mx-auto lg:mx-0 max-w-2xl">
                A escolha da janela acústica ideal depende de vários fatores, como o nível de ruído externo, o tipo de ambiente (quarto, sala, escritório) e a estética desejada. Nossa equipe técnica está pronta para avaliar seu caso e recomendar a melhor solução.
              </p>
              <ul className="space-y-4 inline-block text-left">
                {[
                  "Avaliação do tipo de ruído",
                  "Análise do ambiente e ventilação",
                  "Escolha do vidro e da esquadria",
                  "Medição técnica no local"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-accent" />
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-10 rounded-[3rem] border border-white/20 text-center">
              <h3 className="text-2xl font-bold mb-6">Precisa de ajuda?</h3>
              <p className="opacity-80 mb-8 max-w-sm mx-auto">Solicite uma consultoria técnica gratuita e descubra qual modelo melhor atende sua necessidade.</p>
              <Link 
                to="/orcamento" 
                onClick={() => trackWhatsAppClick("Falar com consultor", "/orcamento", "produtos")}
                data-gtm-event="whatsapp_click"
                data-conversion="whatsapp"
                data-button-location="produtos"
                className="w-full bg-accent text-primary py-4 rounded-xl font-bold text-center inline-block hover:bg-white transition-all"
              >
                Falar com consultor
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 md:py-24 px-6 bg-white overflow-hidden">
        <div className="container mx-auto">
          <div className="bg-primary rounded-[3rem] p-12 lg:p-24 relative overflow-hidden text-center text-white">
            <div className="absolute top-0 right-0 w-1/3 h-full bg-accent/20 skew-x-12 translate-x-1/2" />
            <div className="relative z-10">
              <h2 className="text-2xl md:text-3xl lg:text-5xl font-bold mb-8">Pronto para recuperar o silêncio?</h2>
              <p className="text-xl opacity-80 font-normal mb-12 max-w-2xl mx-auto leading-relaxed">Fale com a STOP SOM e receba uma solução personalizada para o seu espaço.</p>
              <a 
                href="https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento." 
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick("Solicitar orçamento", "https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento.", "cta-final")}
                data-gtm-event="whatsapp_click"
                data-conversion="whatsapp"
                data-button-location="cta-final"
                className="inline-flex items-center gap-3 bg-accent text-primary px-8 md:px-12 py-5 md:py-6 rounded-2xl font-bold text-lg md:text-xl hover:bg-white transition-all transform hover:scale-105 shadow-2xl shadow-black/20"
              >
                <WhatsAppIcon className="w-8 h-8" /> Solicitar orçamento
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* JSON-LD LocalBusiness */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "STOP SOM Janelas Acústicas",
          "image": "https://stopsomesquadrias.com.br/logo.png",
          "@id": "https://stopsomesquadrias.com.br",
          "url": "https://stopsomesquadrias.com.br",
          "telephone": "+55 21 99269-8354",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Rua Itatinga, 511",
            "addressLocality": "Duque de Caxias",
            "addressRegion": "RJ",
            "postalCode": "25000-000",
            "addressCountry": "BR"
          },
          "areaServed": {
            "@type": "State",
            "name": "Rio de Janeiro"
          },
          "description": "Empresa especializada em janelas acústicas, esquadrias acústicas e soluções antirruído sob medida no Rio de Janeiro, com fábrica em Duque de Caxias - RJ."
        })}
      </script>
    </div>
    </>
  );
}
